// ─── Sitelinks section — repeatable cards (Search & PMax only) ──────────────
// Each sitelink: Title (≤25), Description line 1 (≤35), Description line 2 (≤35),
// Final URL. Optional — 0 to 10 per variant. Field-issue keys mirror the backend
// (sitelinks[i].title, .description1, .description2, .final_url) so server-side
// validation merges cleanly with client-side checks.

import { Plus, Trash2 } from 'lucide-react';
import {
  FieldLabel,
  CharCount,
  InlineIssue,
  inputClass,
  issueBorder,
} from '../../ad-details/shared';
import { SITELINK_MAX, SITELINK_TITLE_MAX, SITELINK_DESC_MAX } from './sitelinksConstants';
import type { FieldIssue, Sitelink } from './types';

const EMPTY_SITELINK: Sitelink = {
  title: '',
  description1: '',
  description2: '',
  finalUrl: '',
};

interface SitelinksSectionProps {
  values: Sitelink[];
  onChange: (values: Sitelink[]) => void;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  readOnly?: boolean;
}

export function SitelinksSection({
  values,
  onChange,
  fieldIssues,
  showErrors,
  showWarnings,
  readOnly = false,
}: SitelinksSectionProps) {
  function updateAt(index: number, patch: Partial<Sitelink>) {
    onChange(values.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function addItem() {
    if (values.length >= SITELINK_MAX) return;
    onChange([...values, { ...EMPTY_SITELINK }]);
  }

  function removeAt(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  const sectionIssue = fieldIssues['sitelinks'];
  const hasSectionError = !!(sectionIssue?.errors.length && showErrors);

  return (
    <fieldset disabled={readOnly}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Sitelinks
          </p>
          <span className="normal-case font-normal tracking-normal text-[10px] text-slate-300">
            (optional · up to {SITELINK_MAX})
          </span>
        </div>
        {values.length > 0 && (
          <span className="text-[10px] text-slate-300 tabular-nums">
            {values.length}/{SITELINK_MAX}
          </span>
        )}
      </div>

      <div className="border-l-2 border-slate-200 pl-3 py-1 space-y-3">
        {values.length === 0 && (
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors py-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Sitelink
          </button>
        )}

        {values.map((sitelink, index) => {
          const titleIssue = fieldIssues[`sitelinks[${index}].title`];
          const desc1Issue = fieldIssues[`sitelinks[${index}].description1`];
          const desc2Issue = fieldIssues[`sitelinks[${index}].description2`];
          const urlIssue = fieldIssues[`sitelinks[${index}].final_url`];

          return (
            <div
              key={index}
              className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-slate-500">Sitelink {index + 1}</p>
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove sitelink"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Title */}
              <div>
                <FieldLabel
                  label="Title"
                  right={<CharCount value={sitelink.title} limit={SITELINK_TITLE_MAX} />}
                />
                <input
                  type="text"
                  value={sitelink.title}
                  onChange={(e) => updateAt(index, { title: e.target.value })}
                  placeholder="e.g. Build your Ford Puma"
                  className={`${inputClass} ${issueBorder(titleIssue, showErrors, showWarnings)}`}
                />
                <InlineIssue
                  issue={titleIssue}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                />
              </div>

              {/* Description line 1 */}
              <div>
                <FieldLabel
                  label="Description line 1"
                  right={<CharCount value={sitelink.description1} limit={SITELINK_DESC_MAX} />}
                />
                <input
                  type="text"
                  value={sitelink.description1}
                  onChange={(e) => updateAt(index, { description1: e.target.value })}
                  placeholder="Short description line 1"
                  className={`${inputClass} ${issueBorder(desc1Issue, showErrors, showWarnings)}`}
                />
                <InlineIssue
                  issue={desc1Issue}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                />
              </div>

              {/* Description line 2 */}
              <div>
                <FieldLabel
                  label="Description line 2"
                  right={<CharCount value={sitelink.description2} limit={SITELINK_DESC_MAX} />}
                />
                <input
                  type="text"
                  value={sitelink.description2}
                  onChange={(e) => updateAt(index, { description2: e.target.value })}
                  placeholder="Short description line 2"
                  className={`${inputClass} ${issueBorder(desc2Issue, showErrors, showWarnings)}`}
                />
                <InlineIssue
                  issue={desc2Issue}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                />
              </div>

              {/* Final URL */}
              <div>
                <FieldLabel label="Final URL" />
                <input
                  type="url"
                  value={sitelink.finalUrl}
                  onChange={(e) => updateAt(index, { finalUrl: e.target.value })}
                  placeholder="https://ford.com/build"
                  className={`${inputClass} ${issueBorder(urlIssue, showErrors, showWarnings)}`}
                />
                <InlineIssue issue={urlIssue} showErrors={showErrors} showWarnings={showWarnings} />
              </div>
            </div>
          );
        })}

        {values.length > 0 && values.length < SITELINK_MAX && (
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Sitelink
          </button>
        )}
      </div>

      {hasSectionError &&
        sectionIssue!.errors.map((msg, i) => (
          <p key={`e${i}`} className="text-xs text-red-600 mt-1">
            ✕ {msg}
          </p>
        ))}
    </fieldset>
  );
}
